import { useState, useEffect, useCallback } from 'react';
import {
  isWebPushSupported,
  getWebPushStatus,
  subscribeToWebPush,
  unsubscribeFromWebPush,
  sendTestWebPush,
  WebPushStatus,
} from '../services/webPushService';

export function useWebPush() {
  const [status, setStatus] = useState<WebPushStatus>({
    isSupported: false,
    permission: 'default',
    isSubscribed: false,
    subscription: null,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    try {
      const currentStatus = await getWebPushStatus();
      setStatus(currentStatus);
    } catch (e) {
      console.warn('Erro ao checar status de push:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();

    const handleStatusChange = () => {
      checkStatus();
    };

    window.addEventListener('literafan:push_status_change', handleStatusChange);
    return () => {
      window.removeEventListener('literafan:push_status_change', handleStatusChange);
    };
  }, [checkStatus]);

  const subscribe = async (topics: string[] = ['articles', 'chapters', 'all'], email?: string) => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await subscribeToWebPush(topics, email);
      if (res.success) {
        setToastMessage(res.message || 'Notificações de novos artigos e capítulos ativadas!');
        await checkStatus();
        return true;
      } else {
        setError(res.error || 'Não foi possível ativar as notificações.');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'Erro inesperado ao ativar notificações push.');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const unsubscribe = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await unsubscribeFromWebPush();
      if (res.success) {
        setToastMessage('Notificações push desativadas.');
        await checkStatus();
        return true;
      } else {
        setError(res.error || 'Erro ao desativar notificações.');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao desativar notificações.');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const testPush = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await sendTestWebPush();
      if (res.success) {
        setToastMessage(res.message || 'Notificação push enviada!');
        return true;
      } else {
        setError(res.error || 'Erro ao enviar notificação de teste.');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar notificação.');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  return {
    isSupported: status.isSupported,
    permission: status.permission,
    isSubscribed: status.isSubscribed,
    isLoading,
    actionLoading,
    error,
    toastMessage,
    clearToast: () => setToastMessage(null),
    clearError: () => setError(null),
    subscribe,
    unsubscribe,
    testPush,
    refreshStatus: checkStatus,
  };
}
